/**
 * Chinese move notation (WXF style), for spoken announcements.
 *
 * Red counts files right-to-left with 九…一, Black counts them from Black's own
 * right with 1…9. A move is "piece + which one + 平/进/退 + target", where the
 * final number is the destination file for a piece that moves diagonally
 * (馬/相/仕) and the number of ranks travelled for one that moves straight
 * (車/炮/兵/將). When two identical pieces share a file the origin file is
 * useless, so they are named 前/后 (and stacked pawns 一二三四五) instead.
 *
 * The position passed in must be the one BEFORE the move is played.
 */

import { Color, PieceType, fileOf, rankOf, type Move, type Position } from "@xq/rules";

const RED_NAMES = ["帥", "仕", "相", "馬", "俥", "炮", "兵"];
const BLACK_NAMES = ["將", "士", "象", "馬", "車", "砲", "卒"];
const CN_NUM = ["一", "二", "三", "四", "五", "六", "七", "八", "九"];
const AR_NUM = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

/** File as that side counts it: 1 is the file furthest to that player's right. */
function fileNumber(file: number, color: Color): number {
  return color === Color.Red ? 9 - file : file + 1;
}
const numeral = (n: number, color: Color) => (color === Color.Red ? CN_NUM[n - 1]! : AR_NUM[n - 1]!);

/** Pieces that name their destination file rather than a distance. */
const DIAGONAL = new Set<PieceType>([PieceType.Horse, PieceType.Elephant, PieceType.Advisor]);

/** e.g. 炮二平五 / 馬8进7 — the move as a player would say it. */
export function moveToChinese(pos: Position, m: Move): string {
  const p = pos.pieceAt(m.from);
  if (!p) return "";
  const color = p.color;
  const names = color === Color.Red ? RED_NAMES : BLACK_NAMES;
  const name = names[p.type]!;
  const fromFile = fileOf(m.from), toFile = fileOf(m.to);
  const fromRank = rankOf(m.from), toRank = rankOf(m.to);

  // Which of the identical pieces is it? Only pieces stacked on the same file
  // need front/back naming; everything else is identified by its file.
  const sameFile: number[] = [];
  for (let sq = 0; sq < 90; sq++) {
    const q = pos.pieceAt(sq);
    if (q && q.color === color && q.type === p.type && fileOf(sq) === fromFile) sameFile.push(sq);
  }
  // Sort from that player's front (furthest advanced) to their back.
  sameFile.sort((a, b) => (color === Color.Red ? rankOf(b) - rankOf(a) : rankOf(a) - rankOf(b)));

  let subject: string;
  if (sameFile.length < 2) {
    subject = name + numeral(fileNumber(fromFile, color), color);
  } else if (sameFile.length === 2) {
    subject = (sameFile[0] === m.from ? "前" : "后") + name;
  } else {
    // Three or more (only ever pawns): 前 / 二 / 三 … / 后.
    const i = sameFile.indexOf(m.from);
    const rank = i === 0 ? "前" : i === sameFile.length - 1 ? "后" : CN_NUM[i]!;
    subject = rank + name;
  }

  if (fromRank === toRank) return `${subject}平${numeral(fileNumber(toFile, color), color)}`;
  const forward = color === Color.Red ? toRank > fromRank : toRank < fromRank;
  const verb = forward ? "进" : "退";
  const amount = DIAGONAL.has(p.type)
    ? fileNumber(toFile, color)
    : Math.abs(toRank - fromRank);
  return `${subject}${verb}${numeral(amount, color)}`;
}

const EN_NAMES = ["General", "Advisor", "Elephant", "Horse", "Chariot", "Cannon", "Soldier"];
const FILES = "abcdefghi";
const coordOf = (sq: number) => `${FILES[fileOf(sq)]}${rankOf(sq)}`;

/** "Cannon h2 to e2" — the English announcement, read out coordinate by coordinate. */
export function moveToSpokenEn(pos: Position, m: Move): string {
  const p = pos.pieceAt(m.from);
  if (!p) return "";
  const side = p.color === Color.Red ? "Red" : "Black";
  return `${side} ${EN_NAMES[p.type]} ${coordOf(m.from).toUpperCase()} to ${coordOf(m.to).toUpperCase()}`;
}
